class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.timerLifecycle?.abort();
    this.timerLifecycle = new AbortController();
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopFomoTimer();
      else this.initFomoTimer();
    }, { signal: this.timerLifecycle.signal });
    window.addEventListener('pageshow', () => this.initFomoTimer(), { signal: this.timerLifecycle.signal });
    this.syncShippingProgress(false);
    this.initFomoTimer();
  }

  disconnectedCallback() {
    this.cartRefreshController?.abort();
    this.stopFomoTimer();
    this.timerLifecycle?.abort();
    clearTimeout(this.openTimeout);
    this.openTimeout = null;
    this.pendingRewardMilestones = null;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active') || this.openTimeout) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this.openTimeout = setTimeout(() => {
      this.openTimeout = null;
      this.classList.add('animate', 'active');
      this.syncShippingProgress(false);
      this.initFomoTimer();
      if (this.pendingRewardMilestones) {
        const previous = this.pendingRewardMilestones;
        this.pendingRewardMilestones = null;
        this.celebrateRewardMilestones(previous);
      }
      this.syncFreeGift();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          (this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : null) ||
          document.getElementById('CartDrawer') ||
          this.querySelector('.drawer__inner');
        if (!containerToTrapFocusOn) return;
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    /* themeScrollLock بيعمل position: fixed كمان — overflow: hidden
       لوحدها مش بتوقف تمرير الصفحة على iOS Safari. */
    if (window.themeScrollLock) window.themeScrollLock.lock();
    else document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  syncShippingProgress(animateChange = false) {
    const fill = this.querySelector('[data-cart-shipping-progress]');
    if (!fill) {
      this.shippingProgress = undefined;
      return;
    }

    const target = Number.parseFloat(fill.dataset.cartShippingProgress || '0');
    if (!Number.isFinite(target)) return;

    const previous = this.shippingProgress;
    this.shippingProgress = target;
    fill.style.inlineSize = target + '%';

    if (
      !animateChange ||
      !Number.isFinite(previous) ||
      previous === target ||
      typeof fill.animate !== 'function' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    fill.animate(
      [{ inlineSize: previous + '%' }, { inlineSize: target + '%' }],
      {
        duration: 620,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      },
    );
  }

  getRewardState() {
    const offer = this.querySelector('[data-free-gift]');
    return {
      shipping: !!offer && offer.classList.contains('cart-shipping-card--shipping'),
      gift: !!offer && offer.classList.contains('cart-shipping-card--complete'),
    };
  }

  celebrateRewardMilestones(previous) {
    const offer = this.querySelector('[data-free-gift]');
    if (!previous || offer?.dataset.celebrateRewards !== 'true') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const current = this.getRewardState();
    if ((!previous.shipping && current.shipping) || (!previous.gift && current.gift)) {
      if (!this.classList.contains('active')) {
        this.pendingRewardMilestones = previous;
        return;
      }
    }
    const markers = this.querySelectorAll('.cart-shipping-card__marker-node');
    if (!previous.shipping && current.shipping && markers[0]) this.spawnRewardConfetti(markers[0]);
    if (!previous.gift && current.gift && markers[1]) this.spawnRewardConfetti(markers[1]);
  }

  spawnRewardConfetti(marker) {
    marker.querySelector('.cart-shipping-card__confetti')?.remove();
    const layer = document.createElement('span');
    layer.className = 'cart-shipping-card__confetti';
    layer.setAttribute('aria-hidden', 'true');
    const colors = ['#F4A9BA', '#F7C98A', '#A8D9C3', '#B9B5E5', '#EFCB75'];
    const fragment = document.createDocumentFragment();

    for (let index = 0; index < 30; index++) {
      const angle = index * 2.39996;
      const distance = 45 + (index % 5) * 12;
      const piece = document.createElement('span');
      piece.className = 'cart-shipping-card__confetti-piece';
      piece.style.setProperty('--confetti-color', colors[index % colors.length]);
      piece.style.setProperty('--confetti-x', Math.round(Math.cos(angle) * distance) + 'px');
      piece.style.setProperty('--confetti-y', Math.round(Math.sin(angle) * distance - 12) + 'px');
      piece.style.setProperty('--confetti-rotation', (index % 2 ? 240 : -210) + 'deg');
      piece.style.setProperty('--confetti-delay', (index % 5) * 18 + 'ms');
      fragment.appendChild(piece);
    }

    layer.appendChild(fragment);
    marker.appendChild(layer);
    window.setTimeout(() => layer.remove(), 1100);
  }

  invalidateCartRefresh() {
    this.cartRefreshRevision = (this.cartRefreshRevision || 0) + 1;
    this.cartRefreshController?.abort();
  }

  beginCartMutation() {
    this.cartMutationPending = (this.cartMutationPending || 0) + 1;
    this.invalidateCartRefresh();
  }

  endCartMutation() {
    this.cartMutationPending = Math.max(0, (this.cartMutationPending || 0) - 1);
    if (!this.cartMutationPending && this.cartRefreshQueued) {
      this.cartRefreshQueued = false;
      this.querySelector('cart-drawer-items')?.onCartUpdate();
    }
  }
  async syncFreeGift() {
    if (this.freeGiftSyncing) return;

    const offer = this.querySelector('[data-free-gift]');
    if (!offer) return;

    const variantId = Number.parseInt(offer.dataset.giftVariantId || '0', 10);
    const eligible = offer.dataset.giftEligible === 'true';
    const giftLineKeys = (offer.dataset.giftLineKeys || '').split(',').filter(Boolean);
    const selectedGiftKey = offer.dataset.selectedGiftKey || '';
    const selectedGiftQuantity = Number.parseInt(offer.dataset.selectedGiftQuantity || '0', 10);
    const sections = this.getSectionsToRender().map((section) => section.id);
    let endpoint = '';
    let payload = null;

    if (!eligible || !variantId) {
      if (!giftLineKeys.length) return;
      endpoint = 'cart/update.js';
      payload = {
        updates: Object.fromEntries(giftLineKeys.map((key) => [key, 0])),
      };
    } else if (!selectedGiftKey) {
      if (giftLineKeys.length) {
        endpoint = 'cart/update.js';
        payload = {
          updates: Object.fromEntries(giftLineKeys.map((key) => [key, 0])),
        };
      } else {
        endpoint = 'cart/add.js';
        payload = {
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _cart_free_gift: 'true',
              },
            },
          ],
        };
      }
    } else {
      const updates = Object.fromEntries(
        giftLineKeys.filter((key) => key !== selectedGiftKey).map((key) => [key, 0]),
      );

      if (selectedGiftQuantity !== 1) updates[selectedGiftKey] = 1;
      if (!Object.keys(updates).length) return;

      endpoint = 'cart/update.js';
      payload = { updates };
    }

    this.freeGiftSyncing = true;
    this.beginCartMutation();

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...payload,
          sections,
          sections_url: window.location.pathname,
        }),
      });

      const state = await response.json();
      if (!response.ok) throw new Error(state.description || state.message || 'Unable to update free gift');
      if (state.sections) this.renderContents(state);
    } catch (error) {
      console.error('Free gift sync failed:', error);
    } finally {
      this.freeGiftSyncing = false;
      this.endCartMutation();
    }
  }

  stopFomoTimer() {
    if (this.fomoTimerInterval) clearInterval(this.fomoTimerInterval);
    this.fomoTimerInterval = null;
  }

  initFomoTimer() {
    this.stopFomoTimer();
    const storageKey = 'cart_reward_offer_deadline_v2:' + (window.Shopify?.shop || window.location.hostname);
    const timer = this.querySelector('[data-fomo-timer]');
    if (!timer) {
      // A cart below the offer goal (including an empty cart) ends this offer cycle.
      this.offerTimerRecord = null;
      try { sessionStorage.removeItem(storageKey); } catch (error) {}
      return;
    }
    timer.hidden = true;
    if (!this.classList.contains('active') || document.hidden) return;
    const minutesElement = timer.querySelector('[data-fomo-minutes]');
    const secondsElement = timer.querySelector('[data-fomo-seconds]');
    if (!minutesElement || !secondsElement) return;

    const parsedMinutes = Number.parseInt(timer.dataset.minutes || '10', 10);
    const durationMs = (Number.isFinite(parsedMinutes) ? Math.min(30, Math.max(3, parsedMinutes)) : 10) * 60000;
    const offerKey = timer.dataset.offerKey || '';
    let record = this.offerTimerRecord;
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) record = JSON.parse(stored);
    } catch (error) {}
    if (!record || record.offerKey !== offerKey || record.durationMs !== durationMs ||
        !Number.isFinite(record.endTime) || record.endTime > Date.now() + durationMs + 1000) {
      record = { endTime: Date.now() + durationMs, durationMs, offerKey };
      try { sessionStorage.setItem(storageKey, JSON.stringify(record)); } catch (error) {}
    }
    // Keep an in-memory fallback for browsers that block session storage.
    this.offerTimerRecord = record;
    const updateTimer = () => {
      const remainingMs = record.endTime - Date.now();
      if (remainingMs <= 0 || !this.classList.contains('active') || !this.isConnected ||
          document.hidden || this.querySelector('[data-fomo-timer]') !== timer) {
        timer.hidden = true;
        this.stopFomoTimer();
        return false;
      }
      const remaining = Math.ceil(remainingMs / 1000);
      minutesElement.textContent = String(Math.floor(remaining / 60)).padStart(2, '0');
      secondsElement.textContent = String(remaining % 60).padStart(2, '0');
      timer.hidden = false;
      return true;
    };
    // Expiry stays expired until a new qualifying cycle; reopening never resets the countdown.
    if (updateTimer()) this.fomoTimerInterval = setInterval(updateTimer, 1000);
  }
  close() {
    clearTimeout(this.openTimeout);
    this.openTimeout = null;
    this.pendingRewardMilestones = null;
    this.stopFomoTimer();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    if (window.themeScrollLock) window.themeScrollLock.unlock();
    else document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.invalidateCartRefresh();
    const previousRewards = this.getRewardState();
    if (Number.isFinite(parsedState.item_count)) {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    } else {
      this.classList.remove('is-empty');
    }
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    document.querySelector('cart-items')?.classList.toggle('is-empty', parsedState.item_count === 0);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', parsedState.item_count === 0);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // open() bails out when the drawer is already showing, which is exactly
      // the add-to-cart-from-an-open-drawer case — the one where the bar most
      // needs to move. Replay it here for that path only; when the drawer was
      // closed, open() does it.
      const wasOpen = this.classList.contains('active');
      this.open();
      this.syncShippingProgress(wasOpen);
      this.celebrateRewardMilestones(previousRewards);
      this.initFomoTimer();
      this.syncFreeGift();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
    ['main-cart-items', 'main-cart-footer'].forEach((id) => {
      const element = document.getElementById(id);
      if (element?.dataset.id) {
        sections.push({ id: element.dataset.id, selector: '#' + id + ' .js-contents' });
      }
    });
    return sections;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
    ['main-cart-items', 'main-cart-footer'].forEach((id) => {
      const element = document.getElementById(id);
      if (element?.dataset.id) {
        sections.push({ id, section: element.dataset.id, selector: '.js-contents' });
      }
    });
    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

